import {ComboBox, FlexColumn, FlexRow, Icon, IconTooltip, Label, Text} from '@forecast-it/design-system';
import React from 'react';
import {useIntl} from 'react-intl';
import {LabelWithTooltipField} from './ClientContactModal.styles';
import {ProjectBadge, ProjectListItem, RemoveButton} from './ProjectAccessField.styles';
import type {ProjectOption} from './ClientContactModal';

export type ProjectAccessFieldProps = {
	availableProjects: ProjectOption[];
	selectedProjects: ProjectOption[];
	isLoading: boolean;
	onAdd: (projectId: string) => void;
	onRemove: (projectId: string) => void;
	labelMessageId?: string;
	tooltipTitleMessageId?: string;
	tooltipDetailsMessageId?: string;
};

const ProjectAccessField = ({
	availableProjects,
	selectedProjects,
	isLoading,
	onAdd,
	onRemove,
	labelMessageId = 'client_contact.field.project_access',
	tooltipTitleMessageId = 'client_contact.tooltip.project_access.title',
	tooltipDetailsMessageId = 'client_contact.tooltip.project_access',
}: ProjectAccessFieldProps): JSX.Element => {
	const {formatMessage} = useIntl();

	return (
		<div data-testid="project-access-field">
			<FlexColumn gap={'s'}>
				<LabelWithTooltipField>
					<ComboBox
						data-cy="project-access-combobox"
						aria-label={formatMessage({id: labelMessageId})}
						defaultItems={availableProjects}
						onChange={onAdd}
						disabled={isLoading}
						placeholder={
							isLoading
								? formatMessage({id: 'client_contact.placeholder.loading_projects'})
								: formatMessage({id: 'client_contact.placeholder.select_project'})
						}
						width={'flex'}
					>
						{item => (
							<ComboBox.Item textValue={item.name}>
								<FlexRow>
									<Text noWrap>{item.name}</Text>
									<div
										style={{
											marginLeft: 'auto',
											padding: '0.125rem 0.5rem',
											borderRadius: '0.25rem',
											backgroundColor: item.color,
										}}
									>
										<Text>{item.prefix}</Text>
									</div>
								</FlexRow>
							</ComboBox.Item>
						)}
					</ComboBox>
					<FlexRow gap={'xs'} alignItems={'center'}>
						<Label>{formatMessage({id: labelMessageId})}</Label>
						<IconTooltip>
							<IconTooltip.Item
								title={formatMessage({id: tooltipTitleMessageId})}
								details={formatMessage({id: tooltipDetailsMessageId})}
							/>
						</IconTooltip>
					</FlexRow>
				</LabelWithTooltipField>
				{selectedProjects.map(project => (
					<ProjectListItem key={project.id}>
						<ProjectBadge $color={project.color ?? '#c4c4c4'}>{project.prefix ?? 'P'}</ProjectBadge>
						<Text noWrap>{project.name}</Text>
						<RemoveButton onClick={() => onRemove(project.id)} aria-label={`Remove ${project.name}`}>
							<Icon icon={'close'} size={'s'} />
						</RemoveButton>
					</ProjectListItem>
				))}
			</FlexColumn>
		</div>
	);
};

export default ProjectAccessField;
